import { createContext, useContext, useEffect, useState, ReactNode } from "react";
import { User, Session } from "@supabase/supabase-js";
import { supabase } from "@/integrations/supabase/client";
import { clearLocalAuthSession, getSessionWithTimeout, isInvalidRefreshTokenError, signInWithRetry } from "@/lib/auth-errors";

interface AuthContextType {
  user: User | null;
  session: Session | null;
  loading: boolean;
  signUp: (email: string, password: string) => Promise<{ error: Error | null }>;
  signIn: (email: string, password: string) => Promise<{ error: Error | null }>;
  signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let mounted = true;

    // Set up auth state listener FIRST
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (event, session) => {
        if (!mounted) return;
        setSession(session);
        setUser(session?.user ?? null);
        setLoading(false);

        // Update last_login_at and track device on sign in
        if (event === "SIGNED_IN" && session?.user) {
          // Only log once per browser session to avoid duplicate login events
          const sessionKey = `login_logged_${session.user.id}_${session.access_token?.slice(-8)}`;
          const alreadyLogged = sessionStorage.getItem(sessionKey);

          if (alreadyLogged) return;
          sessionStorage.setItem(sessionKey, "1");

          // Defer side-effects so they never block the auth callback or UI render
          setTimeout(() => {
            const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
              navigator.userAgent
            );
            const deviceType = isMobile ? "mobile" : "desktop";

            supabase
              .from("organization_members")
              .update({ last_login_at: new Date().toISOString() })
              .eq("user_id", session.user.id)
              .then(() => {});

            supabase
              .from("organization_members")
              .select("organization_id")
              .eq("user_id", session.user.id)
              .then(({ data: orgs }) => {
                if (orgs && orgs.length > 0) {
                  const events = orgs.map((org) => ({
                    user_id: session.user.id,
                    organization_id: org.organization_id,
                    device_type: deviceType,
                    user_agent: navigator.userAgent,
                  }));
                  supabase.from("login_events").insert(events).then(() => {});
                }
              });
          }, 0);
        }
      }
    );

    // THEN check for existing session
    getSessionWithTimeout().then(({ data: { session } }) => {
      if (!mounted) return;
      setSession(session);
      setUser(session?.user ?? null);
      setLoading(false);
    }).catch(async (error) => {
      // Handle invalid/expired refresh tokens gracefully
      if (isInvalidRefreshTokenError(error)) {
        await clearLocalAuthSession();
      }
      if (!mounted) return;
      setSession(null);
      setUser(null);
      setLoading(false);
    });

    return () => {
      mounted = false;
      subscription.unsubscribe();
    };
  }, []);

  const signUp = async (email: string, password: string) => {
    const { error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        emailRedirectTo: window.location.origin,
      },
    });
    return { error };
  };

  const signIn = async (email: string, password: string) => {
    return signInWithRetry(email, password);
  };

  const signOut = async () => {
    await supabase.auth.signOut();
  };

  return (
    <AuthContext.Provider value={{ user, session, loading, signUp, signIn, signOut }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
}